<template>
    <el-container class="property-dashboard app-content-page">
        <el-row class="property-dashboard__title">
            <el-col :span="24">
                <app-i18n code="common.overview" />
            </el-col>
        </el-row>
        <el-row
            class="property-dashboard__container"
            justify="center"
            type="flex"
            v-loading="loading"
        >
            <el-col
                v-for="(stat, i) in statsArray"
                :key="`property-dashboard__col--${i}`"
                :span="8"
            >
                <el-card
                    class="property-dashboard__card"
                    :class="{ 'property-dashboard__card--empty': !stat.count}"
                    :style="{ borderColor: stat.borderColor }"
                >
                    <span
                        class="property-dashboard__card__icon icon--circle"
                        :class="stat.iconClass"
                        :style="{
                            backgroundColor: stat.iconBackgroundColor,
                            color: stat.iconColor
                        }"
                    />

                    <el-tooltip
                        :content="stat.tooltipCaption"
                        placement="bottom"
                        class="property-dashboard__card__tooltip"
                    >
                        <font-awesome-icon
                            :icon="tooltipIcon"
                            size="1x"
                        />
                    </el-tooltip>

                    <template v-if="!!stat.count">
                        <p
                            class="property-dashboard__card__count"
                            v-text="stat.count"
                        />

                        <p
                            class="property-dashboard__card__title"
                            v-text="stat.title"
                        />
                    </template>

                    <template v-else>
                        <p
                            class="property-dashboard__card__title"
                            v-text="stat.emptyCaption"
                        />
                    </template>
                </el-card>
            </el-col>
        </el-row>

        <el-row
            v-loading="subpropertiesLoading"
            v-if="stepsLeft === 0"
            class="property-dashboard__subproperties"
        >
            <el-col
                :span="16"
                class="property-dashboard__subproperties__title"
            >
                <app-i18n :code="!isPropertyNBA ? 'entities.subproperty.label' : 'entities.base.label'" />
            </el-col>
            <el-col
                :span="8"
                align="right"
                class="property-dashboard__subproperties__button"
            >
                <template v-if="!subpropertiesLoading">
                    <router-link
                        v-if="!!subproperties.length"
                        :to="{ path: '/subproperty/list' }"
                    >
                        <el-button type="secondary">
                            <app-i18n code="common.seeAll" />
                        </el-button>
                    </router-link>
                    <router-link
                        v-else
                        :to="{ path: '/subproperty/new' }"
                    >
                        <el-button type="primary">
                            <app-i18n :code="!isPropertyNBA ? 'entities.subproperty.new.title' : 'entities.base.new.title'" />
                        </el-button>
                    </router-link>
                </template>
            </el-col>
            <!-- <app-subproperty-list-filter /> -->
            <app-subproperty-list-table
                v-if="!!subproperties && !!subproperties.length"
                simple
                :max-items="3"
            />
        </el-row>
    </el-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';

import SubpropertyListFilter from '@modules/subproperty/components/subproperty-list-filter.vue';
import SubpropertyListTable from '@modules/subproperty/components/subproperty-list-table.vue';

export default {
    name: 'PropertyDashboard',
    data() {
        return {
            tooltipIcon: faQuestionCircle
        };
    },
    computed: {
        ...mapGetters({
            currentManage: 'auth/manage/currentManage',
            stats: 'property/dashboard/stats',
            statsArray: 'property/dashboard/statsArray',
            loading: 'property/dashboard/loading',
            subproperties: 'subproperty/list/rows',
            subpropertiesLoading: 'subproperty/list/loading',
            isPropertyNBA: 'auth/manage/isPropertyNBA',
            steps: 'property/requirements/steps',
        }),
        propertyId() {
          return this.currentManage.property.id ;
        },
        stepsList() {
          return Object.values(this.steps).filter(step => step.active);
        },
        doneSteps() {
          return this.stepsList.filter(step => step.done);
        },
        stepsTotal() {
          return this.stepsList.length;
        },
        stepsLeft() {
          return this.stepsTotal - this.doneSteps.length;
        },
    },


    mounted() {
        const { propertyId } = this;
        this.doFetch({ propertyId });
        this.doFetchSubproperty();
    },
    methods: {
        ...mapActions('property/dashboard', ['doFetch']),
        ...mapActions({
            doFetchSubproperty: 'subproperty/list/doFetch',
        })
    },
    components: {
        [SubpropertyListTable.name]: SubpropertyListTable,
        [SubpropertyListFilter.name]: SubpropertyListFilter,
    },
}
</script>

<style scoped>
.property-dashboard {
    padding: 0;
    flex-direction: column;
}

.property-dashboard p {
    margin: 0;
}

.property-dashboard__container {
    width: 100%;
    padding: 30px 30px 22px 40px;
    flex-wrap: wrap;
}

.property-dashboard__title {
    padding: 30px 54px 22px;
    font-size: 14px;
    letter-spacing: 0.2px;
    color: #666666;
    border-bottom: 1px solid #cccccc;
}

.property-dashboard__card {
    border: 0;
    border-bottom: 3px solid transparent;
    border-radius: 0;
    box-shadow: -10px 10px 20px 0 rgba(30, 30, 30, 0.05);
    height: 164px;
    margin: 12.5px;
}

.property-dashboard__card__tooltip {
    grid-area: tooltip;
}

.property-dashboard__card__icon {
    grid-area: icon;
    justify-self: center;
}

.property-dashboard__card__title {
    grid-area: title;
    font-size: 14px;
    font-weight: 300;
    text-align: center;
    color: rgba(64, 64, 64, 0.35);
}

.property-dashboard__card__count {
    grid-area: count;
    font-size: 40px;
    font-weight: bold;
    letter-spacing: 1px;
    text-align: center;
}

.property-dashboard__subproperties {
    margin: 30px 54px 40px 54px;
    /* padding-bottom: 40px; */
    box-shadow: -10px 10px 20px 0 rgba(30, 30, 30, 0.05);
}

.property-dashboard__subproperties__title {
    font-size: 20px;
    color: #282d32;
    padding: 25px 40px 15px;
    height: 80px;
    display: flex;
    align-items: center;
}

.property-dashboard__subproperties__button {
    padding: 25px 30px 15px 40px;
    height: 80px;
}
</style>

<style>
.property-dashboard__card .el-card__body {
    display: grid;
    grid-template-columns: 20px 1fr 20px;
    grid-template-rows: 50px 45px 20px;
    grid-template-areas:
        'arrow icon tooltip'
        'count count count'
        'title title title';
    padding: 10px;
}

.property-dashboard__card--empty .el-card__body {
    grid-template-rows: 45px 45px 20px;
    grid-template-areas:
        'arrow . tooltip'
        'icon icon icon'
        'title title title';
}
</style>

